export interface WinScreenCbs {
  /** Same settings, fresh seed. */
  onRematch(): void;
  /** Leave the match behind and open the main menu. */
  onMenu(): void;
  /** Friend room: back to the room lobby (the room is still there — do NOT leave it). */
  onOk(): void;
}

/**
 * Match-end overlay. Buttons are big tappable TEXT (per the no-chrome mobile
 * spec: menus are text lists, gameplay is icons).
 */
export class WinScreen {
  private readonly root: HTMLDivElement;
  private readonly title: HTMLHeadingElement;
  private readonly sub: HTMLParagraphElement;
  private readonly rematchBtn: HTMLButtonElement;
  private readonly leaveBtn: HTMLButtonElement;
  /** Set by `show({ okOnly })`: the one button is OK → back to the room, not Menu. */
  private okMode = false;

  constructor(parent: HTMLElement, cbs: WinScreenCbs) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:absolute;inset:0;display:none;align-items:center;justify-content:center;' +
      'flex-direction:column;gap:14px;background:rgba(5,8,14,.72);z-index:10;' +
      'color:#fff;font-family:system-ui;cursor:default';

    this.title = document.createElement('h1');
    this.title.style.cssText = 'margin:0;font-size:34px;letter-spacing:.04em;text-shadow:0 2px 12px rgba(0,0,0,.6)';

    this.sub = document.createElement('p');
    this.sub.style.cssText = 'margin:0;opacity:.85;font-size:14px;text-align:center;padding:0 18px';

    const mkBtn = (label: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.style.cssText =
        'min-width:190px;padding:13px 22px;border:1px solid rgba(255,255,255,.35);' +
        'border-radius:12px;background:rgba(255,255,255,.1);color:#fff;' +
        'font:600 16px/1.2 system-ui;cursor:pointer;touch-action:manipulation';
      b.addEventListener('click', () => {
        fn();
        b.blur();
      });
      b.addEventListener('pointerenter', () => (b.style.background = 'rgba(255,255,255,.2)'));
      b.addEventListener('pointerleave', () => (b.style.background = 'rgba(255,255,255,.1)'));
      return b;
    };

    this.rematchBtn = mkBtn('Rematch', cbs.onRematch);
    // In OK mode this same button is the only way out and must return to the
    // room — `onMenu` would leave it (net.leaveRoom), which is exactly wrong.
    this.leaveBtn = mkBtn('Menu', () => (this.okMode ? cbs.onOk() : cbs.onMenu()));
    this.root.append(this.title, this.sub, this.rematchBtn, this.leaveBtn);
    parent.appendChild(this.root);
  }

  /**
   * `leaveOnly` is the opponent-quit result: they are gone, so there is no one
   * to rematch — the single way out is Leave match.
   * `okOnly` is a friend room: the room still exists with both players in it,
   * so the ONE button is OK, and it goes back to the room lobby (re-tune,
   * ready up, play again) instead of leaving.
   * Otherwise: Rematch + Menu. There is no New map anywhere — find match takes
   * its map from the server, a friend room re-tunes from the room, and for
   * Play vs CPU rematch already picks a fresh seed.
   */
  show(title: string, sub: string, opts: { leaveOnly?: boolean; okOnly?: boolean } = {}): void {
    const { leaveOnly = false, okOnly = false } = opts;
    this.okMode = okOnly;
    this.title.textContent = title;
    this.sub.textContent = sub;
    this.rematchBtn.style.display = leaveOnly || okOnly ? 'none' : '';
    this.leaveBtn.textContent = okOnly ? 'OK' : leaveOnly ? '✕  Leave match' : 'Menu';
    this.root.style.display = 'flex';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  /** The result screen owns the view — a room update must not open over it. */
  get visible(): boolean {
    return this.root.style.display !== 'none';
  }
}
